import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { login } from "@/lib/session";
import { HttpError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const state = location.state as { from?: string; expired?: boolean } | null;
  const returnTo = state?.from ?? "/overview";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      qc.invalidateQueries({ queryKey: ["admin-session"] });
      navigate(returnTo, { replace: true });
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.status === 401) setError("Invalid credentials.");
        else if (e.status === 403) setError("This account is not a NuForce admin.");
        else setError(`Login failed (${e.status}).`);
      } else {
        setError("Network error — see browser console.");
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">NuForce</div>
          <h1 className="text-2xl font-semibold">Admin Portal</h1>
          <p className="mt-1 text-sm text-muted-foreground">Internal access only.</p>
        </div>

        {state?.expired && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            Your session expired. Admin tokens last 30 minutes; sign in again to carry on where
            you left off.
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            autoFocus
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-red-300"
          >
            {error}
          </div>
        )}

        <Button type="submit" disabled={submitting || !username || !password} className="w-full">
          {submitting ? "Signing in…" : "Sign in"}
        </Button>

        <p className="text-xs text-muted-foreground">
          Lost access? Contact{" "}
          <a className="underline" href="mailto:platform-ops@nuforce360.com">
            platform-ops@nuforce360.com
          </a>
          .
        </p>
      </form>
    </main>
  );
}
